import React from 'react';
import { useTranslation } from 'react-i18next';

import { YtdSection as YtdData } from '../../services/quoteToCashTypes';

import { compactMoney, formatDate, fullMoney, percent, signed } from './format';
import { LineChart, TooltipRow } from './LineChart';
import { KpiTile, SERIES_COLORS, Variance } from './SectionCard';

interface YtdSectionProps {
  data: YtdData;
  /** Sales has a target line and KPI; invoicing does not */
  kind: 'sales' | 'invoicing';
}

/** Sections 2 and 4: this fiscal year to date, cumulative, against the previous year (and, for sales, the target) */
export const YtdSection: React.FC<YtdSectionProps> = ({ data, kind }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { kpis } = data;
  const fy = data.currentFiscalYear;
  const money = (value: number) => compactMoney(value, lang);
  const variance = (amount: number, pct: number | null) => ({
    amount: signed(money(amount), amount),
    pct: pct === null ? null : signed(percent(pct, lang), pct),
    value: amount,
  });
  const labels = data.series.map((p) =>
    formatDate(p.period, lang, data.view === 'month' ? 'month' : 'day')
  );

  const series = [
    {
      key: 'current',
      label: t('quoteToCash.fy', { year: fy }),
      color: SERIES_COLORS.first,
      values: data.series.map((p) => p.currentFY),
    },
    {
      key: 'previous',
      label: t('quoteToCash.fy', { year: fy - 1 }),
      color: SERIES_COLORS.second,
      values: data.series.map((p) => p.previousFY),
    },
    {
      key: 'target',
      label: t('quoteToCash.ytd.targetLine', {
        year: fy,
      }),
      color: SERIES_COLORS.reference,
      dashed: true,
      values: data.series.map((p) => p.target ?? null),
    },
    // No line, and no legend entry, for a year before Epicor or a missing target
  ].filter((s) => s.values.some((v) => v !== null));

  return (
    <>
      <div
        className={`grid grid-cols-1 gap-4 mb-6 ${kind === 'sales' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}
      >
        <KpiTile
          label={t(`quoteToCash.ytd.${kind}.current`, { year: fy })}
          value={money(kpis.currentYtd)}
          detail={t('quoteToCash.asOf', {
            date: formatDate(data.asOf, lang, 'long'),
          })}
        />
        <KpiTile
          label={t('quoteToCash.ytd.priorSameDate', { year: fy - 1 })}
          value={
            kpis.priorYtdComparable === null
              ? t('quoteToCash.notAvailable')
              : money(kpis.priorYtdComparable)
          }
          detail={
            kpis.priorYtdComparable === null ||
            kpis.priorYearVariance === null ? (
              t('quoteToCash.beforeEpicor')
            ) : (
              <Variance
                {...variance(kpis.priorYearVariance, kpis.priorYearVariancePct)}
                suffix={t('quoteToCash.ytd.vsPriorYear')}
              />
            )
          }
        />
        {kind === 'sales' && (
          <KpiTile
            label={t('quoteToCash.ytd.vsTargetPace')}
            value={
              kpis.targetVariance == null
                ? t('quoteToCash.notAvailable')
                : signed(money(kpis.targetVariance), kpis.targetVariance)
            }
            detail={
              kpis.targetVariance == null || kpis.targetToDate == null ? (
                t('quoteToCash.ytd.noTarget', { year: fy })
              ) : (
                <Variance
                  amount={
                    kpis.targetVariancePct == null
                      ? ''
                      : signed(
                          percent(kpis.targetVariancePct, lang),
                          kpis.targetVariancePct
                        )
                  }
                  pct={null}
                  value={kpis.targetVariance}
                  suffix={t('quoteToCash.ytd.targetToDate', {
                    toDate: money(kpis.targetToDate),
                    target: money(kpis.annualTarget ?? 0),
                  })}
                />
              )
            }
          />
        )}
      </div>
      <LineChart
        ariaLabel={t(`quoteToCash.ytd.${kind}.title`)}
        labels={labels}
        formatValue={(v) => compactMoney(v, lang)}
        series={series}
        renderTooltip={(i) => (
          <>
            {series.map((s) =>
              s.values[i] == null ? null : (
                <TooltipRow
                  key={s.key}
                  color={s.color}
                  dashed={s.dashed}
                  value={fullMoney(s.values[i] as number, lang)}
                  label={s.label}
                />
              )
            )}
          </>
        )}
      />
    </>
  );
};
